import { canonicalizeSegment, localizeSegment, TRANSLATED_PATHS } from './links.js'

/**
 * Languages the site can display.
 *
 * To add a language, add its 2-letter ISO-639-1 code here (and make sure the CMS
 * provides content for it - see src/schema.ts). Routing, links, the language
 * switcher and hreflang metadata are all data-driven from this list.
 */
export const SUPPORTED_LANGUAGES = ['en', 'sv'] as const

export type Language = (typeof SUPPORTED_LANGUAGES)[number]

/** Display name of each language (in that language), as shown in the LanguageSwitcher. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  sv: 'Svenska',
}

/**
 * The default language. Its pages live at the site root with no URL prefix
 * (e.g. `/contact`); every other language is served under its own prefix
 * (e.g. `/sv/contact`). Also used as the CMS fallback locale.
 */
export const DEFAULT_LANGUAGE: Language = 'en'

/** localStorage key holding the visitor's language preference (first-visit detection or switcher choice). */
const STORAGE_KEY = 'mud.language'

/**
 * The site's language model is URL-based: the current route decides the language.
 * The default language is unprefixed at the root; other languages live under a
 * `/<lang>` prefix. These helpers convert between a canonical (default-language)
 * path and its localized variants, and are the single source of truth for links,
 * the switcher, `<html lang>` and hreflang metadata.
 */
class LanguageService {
  /**
   * Localize a canonical (default-language) path for `lang`: translate each slug
   * and add the language prefix. `/contact` -> `/sv/kontakt`; `/` -> `/sv`;
   * the default language stays canonical and unprefixed. Idempotent: a path
   * already in another language is re-resolved from its canonical form.
   *
   * See ROUTE_SEGMENTS in links.ts for the slug map.
   */
  localizePath(path: string, lang: Language): string {
    // Only internal absolute paths are localized; leave mailto:/tel:/http(s)/#anchors as-is.
    if (!path.startsWith('/')) return path
    const canonical = this.delocalizePath(path)
    const segments = canonical
      .split('/')
      .filter(Boolean)
      .map(segment => localizeSegment(segment, lang))
    const localized = segments.length ? `/${segments.join('/')}` : '/'
    if (lang === DEFAULT_LANGUAGE) return localized
    return localized === '/' ? `/${lang}` : `/${lang}${localized}`
  }

  /**
   * The language a pathname belongs to, based on its leading segment.
   * `/sv` or `/sv/...` -> 'sv'; anything else -> the default language.
   */
  languageFromPath(pathname: string): Language {
    const seg = pathname.split('/')[1]
    return SUPPORTED_LANGUAGES.find(l => l !== DEFAULT_LANGUAGE && l === seg) ?? DEFAULT_LANGUAGE
  }

  /**
   * Strip the language prefix and translate slugs back to canonical form.
   * `/sv/kontakt` -> `/contact`, `/sv` -> `/`, `/contact` -> `/contact`.
   * Also strips trailing slashes, so the result is safe to compare against
   * canonical paths (`=== '/contact'`).
   */
  delocalizePath(pathname: string): string {
    pathname = this.normalizePathname(pathname)
    const lang = this.languageFromPath(pathname)
    if (lang === DEFAULT_LANGUAGE) return pathname
    const withoutPrefix = pathname.slice(`/${lang}`.length)
    const segments = withoutPrefix
      .split('/')
      .filter(Boolean)
      .map(slug => canonicalizeSegment(slug, lang))
    return segments.length ? `/${segments.join('/')}` : '/'
  }

  /**
   * Strip trailing slashes: `/contact/` -> `/contact` (`/` stays `/`).
   * React Router's prerenderer renders every page at its trailing-slash URL,
   * so `useLocation().pathname` must be normalized like this before being
   * compared against a canonical or localized path.
   */
  normalizePathname(pathname: string): string {
    return pathname.replace(/\/+$/, '') || '/'
  }

  /**
   * The language of the page a loader is serving, derived from the request URL.
   * Works both at build time (prerendering) and per-request (SSR preview).
   */
  langFromRequest(request: Request): Language {
    return this.languageFromPath(new URL(request.url).pathname)
  }

  /**
   * Whether `lang` has real translated content for `canonicalPath` (the default
   * language is always true). Data-driven from TRANSLATED_PATHS in links.ts.
   * Untranslated localized pages are noindex + excluded from the sitemap/hreflang.
   */
  isTranslated(canonicalPath: string, lang: Language): boolean {
    if (lang === DEFAULT_LANGUAGE) return true
    return TRANSLATED_PATHS[lang]?.includes(canonicalPath) ?? false
  }

  // --- Visitor preference (client-only; no-ops during SSR / static export) ---

  /**
   * The visitor's stored language preference, if any. Its presence marks the
   * visitor as "seen" so first-visit auto-detection runs only once.
   * Set by first-visit detection (HomeLanguageDetector) or the LanguageSwitcher.
   */
  getStoredLanguage(): Language | undefined {
    if (typeof localStorage === 'undefined') return undefined
    try {
      const value = localStorage.getItem(STORAGE_KEY)
      return SUPPORTED_LANGUAGES.includes(value as Language) ? (value as Language) : undefined
    } catch {
      return undefined
    }
  }

  /** Persist the visitor's language preference. */
  setStoredLanguage(lang: Language): void {
    if (typeof localStorage === 'undefined') return
    try {
      localStorage.setItem(STORAGE_KEY, lang)
    } catch {
      // localStorage can be unavailable (private mode / blocked) - ignore
    }
  }

  /**
   * The visitor's preferred supported language from the browser
   * (`navigator.languages`), matched on the primary subtag (`en-US` -> `en`),
   * falling back to DEFAULT_LANGUAGE when none are supported.
   */
  detectBrowserLanguage(): Language {
    if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE
    const candidates = navigator.languages?.length ? navigator.languages : [navigator.language]
    for (const candidate of candidates) {
      const primary = candidate.toLowerCase().split('-')[0]
      const match = SUPPORTED_LANGUAGES.find(l => l === primary)
      if (match) return match
    }
    return DEFAULT_LANGUAGE
  }
}

export const languageService = new LanguageService()
